import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PresignedImage from '../common/PresignedImage';
import FileNameWithExtension from './FileNameWithExtension';
import { usePrefersCoarsePointer } from '../../utils/usePrefersCoarsePointer';
import FileTypeIcon from '../common/FileTypeIcon';
import {
  formatFileSize,
  getFileExtensionLabel,
  getFilePreviewKind,
  splitFileNameParts,
} from '../../utils/fileUtils';
import { getPresignedFileUrl } from '../../services/fileUrlService';
import { isOverSharedDragGrip } from '../../utils/sharedFilesDrag';

const SHOW_DELAY_MS = 220;
const HIDE_DELAY_MS = 120;
const PREVIEW_WIDTH = 320;
const PREVIEW_GAP = 14;

const FileHoverPreviewContent = ({
  attachment,
  canRename,
  isEditing,
  editName,
  onEditNameChange,
  onStartEdit,
  onCancelEdit,
  onSaveRename,
  isSaving,
  fileExtension,
}) => {
  const previewKind = getFilePreviewKind(attachment);
  const [pdfUrl, setPdfUrl] = useState(null);
  const ext = getFileExtensionLabel(attachment?.fileName);

  useEffect(() => {
    if (previewKind !== 'pdf' || !attachment?.fileUrl) {
      setPdfUrl(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = await getPresignedFileUrl(attachment.fileUrl, {
          originalName: attachment.fileName,
        });
        if (!cancelled) setPdfUrl(url);
      } catch {
        if (!cancelled) setPdfUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment?.fileUrl, attachment?.fileName, previewKind]);

  return (
    <div className="flex flex-col">
      <div className="bg-gray-50 dark:bg-gray-900 flex items-center justify-center min-h-[120px] max-h-[420px] overflow-hidden">
        {previewKind === 'image' && (
          <PresignedImage
            fileKey={attachment.fileUrl}
            originalName={attachment.fileName}
            alt={attachment.fileName}
            className="max-w-[320px] max-h-[380px] w-auto h-auto object-contain"
            loadingClassName="w-48 h-48 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"
          />
        )}

        {previewKind === 'pdf' && (
          <div className="w-[300px] h-[380px] overflow-hidden bg-white dark:bg-gray-800">
            {pdfUrl ? (
              <iframe
                title={attachment.fileName}
                src={`${pdfUrl}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                className="w-full h-full border-0 pointer-events-none"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse">
                <span className="text-xs text-gray-400">Loading preview…</span>
              </div>
            )}
          </div>
        )}

        {previewKind === 'generic' && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center">
            <FileTypeIcon attachment={attachment} size="lg" className="!w-16 !h-16 !text-sm" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 truncate max-w-full">
              {attachment?.fileName || 'File'}
            </span>
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <div className="flex flex-1 min-w-0 items-center border border-primary-500 rounded overflow-hidden bg-white dark:bg-gray-900">
              <input
                type="text"
                value={editName}
                onChange={(e) => onEditNameChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-xs focus:outline-none bg-transparent text-gray-900 dark:text-white"
                autoFocus
                disabled={isSaving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveRename();
                  if (e.key === 'Escape') onCancelEdit();
                }}
              />
              {fileExtension && (
                <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border-l border-primary-500/30 select-none">
                  {fileExtension.replace(/^\./, '')}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onSaveRename}
              disabled={isSaving}
              className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
              title="Save name"
              aria-label="Save file name"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={isSaving}
              className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
              title="Cancel"
              aria-label="Cancel rename"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 min-w-0">
            <FileNameWithExtension
              fileName={attachment.fileName}
              className="flex-1 min-w-0"
              nameClassName="text-xs font-medium text-gray-900 dark:text-white"
            />
            {canRename && (
              <button
                type="button"
                onClick={onStartEdit}
                className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Rename file"
                aria-label={`Rename ${attachment.fileName}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        )}
        {!isEditing && attachment.fileSize != null && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
            {formatFileSize(attachment.fileSize)}
          </p>
        )}
      </div>
    </div>
  );
};

const FileHoverPreview = ({ attachment, children, className = '', canRename = false, onRename }) => {
  const coarsePointer = usePrefersCoarsePointer();
  const anchorRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const previewH = 460;

    let left = rect.right + PREVIEW_GAP;
    if (left + PREVIEW_WIDTH > viewportW - 8) {
      left = rect.left - PREVIEW_WIDTH - PREVIEW_GAP;
    }
    if (left < 8) {
      left = Math.max(8, (viewportW - PREVIEW_WIDTH) / 2);
    }

    let top = rect.top;
    if (top + previewH > viewportH - 8) {
      top = Math.max(8, viewportH - previewH - 8);
    }

    setPosition({ top, left });
  }, []);

  const scheduleShow = useCallback(() => {
    if (coarsePointer || !attachment?.fileUrl) return;
    clearTimers();
    showTimerRef.current = setTimeout(() => {
      updatePosition();
      setVisible(true);
    }, SHOW_DELAY_MS);
  }, [attachment?.fileUrl, clearTimers, coarsePointer, updatePosition]);

  const scheduleHide = useCallback(() => {
    if (isEditing || isSaving) return;
    clearTimers();
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, HIDE_DELAY_MS);
  }, [clearTimers, isEditing, isSaving]);

  const hideImmediately = useCallback(() => {
    clearTimers();
    setVisible(false);
  }, [clearTimers]);

  const handleAnchorMouseOverCapture = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
    }
  }, [hideImmediately]);

  const handleAnchorMouseEnter = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
      return;
    }
    scheduleShow();
  }, [hideImmediately, scheduleShow]);

  const handleAnchorMouseMove = useCallback((e) => {
    if (isOverSharedDragGrip(e)) {
      hideImmediately();
      return;
    }
    if (!visible && !showTimerRef.current) {
      scheduleShow();
    }
  }, [hideImmediately, scheduleShow, visible]);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, updatePosition]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!visible) {
      setIsEditing(false);
      setEditName('');
      setIsSaving(false);
    }
  }, [visible]);

  const handleStartEdit = useCallback(() => {
    const { baseName } = splitFileNameParts(attachment?.fileName || '');
    setEditName(baseName);
    setIsEditing(true);
    cancelHide();
  }, [attachment?.fileName, cancelHide]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditName('');
  }, []);

  const fileExtension = splitFileNameParts(attachment?.fileName || '').extension;

  const handleSaveRename = useCallback(async () => {
    const trimmedBase = editName.trim();
    if (!trimmedBase || !onRename) {
      handleCancelEdit();
      return;
    }
    const { baseName: currentBase } = splitFileNameParts(attachment?.fileName || '');
    if (trimmedBase === currentBase) {
      handleCancelEdit();
      return;
    }
    setIsSaving(true);
    try {
      await onRename(attachment, trimmedBase);
      handleCancelEdit();
    } catch {
      // Parent handles error notification
    } finally {
      setIsSaving(false);
    }
  }, [attachment, editName, handleCancelEdit, onRename]);

  if (coarsePointer || !attachment?.fileUrl) {
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={className}
        onMouseEnter={handleAnchorMouseEnter}
        onMouseMove={handleAnchorMouseMove}
        onMouseOverCapture={handleAnchorMouseOverCapture}
        onMouseLeave={scheduleHide}
      >
        {children}
      </div>

      {visible && ReactDOM.createPortal(
        <div
          className="fixed z-[9990] w-[320px] rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden pointer-events-auto"
          style={{ top: position.top, left: position.left }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          role="tooltip"
          aria-label={`Preview of ${attachment.fileName}`}
        >
          <FileHoverPreviewContent
            attachment={attachment}
            canRename={canRename && !!onRename}
            isEditing={isEditing}
            editName={editName}
            onEditNameChange={setEditName}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSaveRename={handleSaveRename}
            isSaving={isSaving}
            fileExtension={fileExtension}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default FileHoverPreview;
